/**
 * @file OrderDelayBadge.tsx
 * @description Elapsed time and delay status badge for kitchen order tickets.
 */

import React from 'react';
import { Clock } from 'lucide-react';
import { useOrderElapsedTimer } from '../hooks/useOrderElapsedTimer';
import type { KitchenOrderStatus } from '../../../types/order.types';

interface OrderDelayBadgeProps {
  createdAt: string;
  confirmedAt?: string | null;
  readyAt?: string | null;
  status: KitchenOrderStatus;
}

export const OrderDelayBadge: React.FC<OrderDelayBadgeProps> = ({
  createdAt,
  confirmedAt,
  readyAt,
  status,
}) => {
  // Prep is complete once served to table or cancelled
  const isDone = status === 'done' || status === 'cancelled';

  // For orders at the pass, timer accurately measures duration waiting at pass.
  // For cooking orders, timer accurately measures elapsed prep since sent to kitchen.
  const timerStartTimestamp = status === 'ready'
    ? (readyAt || createdAt)
    : (confirmedAt || createdAt);

  const { elapsedText, tier } = useOrderElapsedTimer(timerStartTimestamp, isDone);

  if (isDone) return null;

  // Active food plated at the pass awaiting runner delivery
  if (status === 'ready') {
    return (
      <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-mono font-bold border bg-purple-950/70 text-purple-300 border-purple-700/60 animate-pulse">
        <Clock className="w-3 h-3 text-purple-400" />
        <span>AT PASS ({elapsedText})</span>
      </div>
    );
  }

  const colorStyles = {
    normal: 'bg-emerald-950/60 text-emerald-400 border-emerald-800/40',
    warning: 'bg-amber-950/60 text-amber-400 border-amber-800/40 animate-pulse',
    delayed: 'bg-rose-950/80 text-rose-300 border-rose-600/60 animate-bounce',
  }[tier];

  return (
    <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-mono font-bold border ${colorStyles}`}>
      <Clock className="w-3 h-3" />
      <span>{elapsedText}</span>
      {tier === 'delayed' && <span className="uppercase text-[9px] tracking-wider ml-0.5">DELAYED</span>}
    </div>
  );
};
